import { useEffect } from 'react'
import {
  addAddressError,
  removeAddressError,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import { selectActiveAddress } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { useDispatch, useSelector } from 'react-redux'
import { meterErrorMessage } from '@/containers/SubscriptionFormPageNew/Components/shared/meterErrorMessage'
import useElectricityMeter from '@/containers/SubscriptionFormPageNew/Components/ElectricityMeter/useElectricityMeter'
import * as Sentry from '@sentry/react'

const PDL_ERROR_FALLBACK = 'Erreur PDL'

const useElectricityMeterListener = () => {
  const dispatch = useDispatch()
  const activeAddress = useSelector(selectActiveAddress)
  const { treatMeterDataInformation, elecMeterUpdate, pdlNo } = useElectricityMeter({ type: 'client' })

  // The meter id error lives on the active address in v2 (same store slot the
  // MeterInput field reads through selectCurrentAddressErrors), so the Enedis
  // websocket callback writes there instead of the removed v1 common errors.
  const setMeterIdError = message => {
    dispatch(addAddressError({ data: { meter_id_elec: [message] }, id: activeAddress }))
  }

  const clearMeterIdError = () => {
    dispatch(removeAddressError({ id: activeAddress, key: 'meter_id_elec' }))
  }

  useEffect(() => {
    if (!pdlNo) {
      return
    }
    window.Echo.channel(`pdl.${pdlNo}`).listen('.pdl.response', e => {
      if (e.status === 'pending') {
        elecMeterUpdate({
          pdlEligibilityPending: true,
        })
      }

      if (e.status === 'error') {
        elecMeterUpdate({
          pdlEligibilityPending: false,
          pdlEligibilityLoading: false,
        })
        setMeterIdError(meterErrorMessage(e.response.exception, PDL_ERROR_FALLBACK))
      }

      if (e.response.elec?.technicalData?.pdl && e.response.elec.technicalData.pdl !== pdlNo) {
        return
      }

      if (e.status === 'success') {
        elecMeterUpdate({
          pdlEligibilityPending: false,
          pdlEligibilityLoading: false,
        })

        clearMeterIdError()
        const pdlInformation = e.response

        if (pdlInformation.exception) {
          if (import.meta.env.PROD) {
            Sentry.captureException(new Error('Electricity meter exception'), { extra: { error: pdlInformation.exception } })
          }
          setMeterIdError(meterErrorMessage(pdlInformation.exception, PDL_ERROR_FALLBACK))
          return
        }
        if (pdlInformation.error && !Array.isArray(pdlInformation.error)) {
          setMeterIdError(meterErrorMessage(pdlInformation.error, PDL_ERROR_FALLBACK))
          return
        }
        treatMeterDataInformation(pdlInformation)
      }
    })
    return () => {
      window.Echo.leaveChannel(`pdl.${pdlNo}`)
    }
  }, [pdlNo])
}

export default useElectricityMeterListener
